import React, { useEffect, useMemo, useState } from "react";
import { Plus, Search, X, FileCheck } from "lucide-react";
import TopBar from "../components/layout/TopBar";
import QuoteForm from "../components/quotes/QuoteForm";
import { useAppState } from "../hooks/useAppState";
import { useAuth } from "../hooks/useAuth";
import {
  QUOTE_STATUSES,
  subscribeQuotes,
  createQuote,
  updateQuote,
  patchQuote,
  deleteQuote,
  buildInvoiceDraftFromQuote,
} from "../services/quotesService";

const STATUS_STYLES = {
  draft: { dot: "bg-neutral-400", light: "bg-neutral-100 text-neutral-700", dark: "bg-neutral-800 text-neutral-300" },
  sent: { dot: "bg-blue-500", light: "bg-blue-50 text-blue-700", dark: "bg-blue-900/30 text-blue-300" },
  accepted: { dot: "bg-emerald-500", light: "bg-emerald-50 text-emerald-700", dark: "bg-emerald-900/30 text-emerald-300" },
  rejected: { dot: "bg-red-500", light: "bg-red-50 text-red-700", dark: "bg-red-900/30 text-red-300" },
  expired: { dot: "bg-amber-500", light: "bg-amber-50 text-amber-700", dark: "bg-amber-900/30 text-amber-300" },
  converted: { dot: "bg-purple-500", light: "bg-purple-50 text-purple-700", dark: "bg-purple-900/30 text-purple-300" },
};

function statusLabel(status) {
  return QUOTE_STATUSES.find((s) => s.id === status)?.label || status;
}

function formatCurrency(value) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(value) || 0);
}

function formatDateIT(isoDate) {
  if (!isoDate) return "—";
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return isoDate;
  return `${day}/${month}/${year}`;
}

export default function QuotesPage() {
  const { user, darkMode, toggleDarkMode, isSynced, clients, createInvoice } = useAppState();
  const { user: authUser } = useAuth();
  const uid = authUser?.uid;

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);

  useEffect(() => {
    if (!uid) return undefined;
    const unsub = subscribeQuotes(uid, (list) => {
      setQuotes(list);
      setLoading(false);
    });
    return () => unsub();
  }, [uid]);

  const years = useMemo(() => {
    const set = new Set(
      quotes
        .map((q) => (q.issueDate ? new Date(q.issueDate).getFullYear() : null))
        .filter(Boolean)
    );
    set.add(new Date().getFullYear());
    return Array.from(set).sort((a, b) => b - a);
  }, [quotes]);

  const clientNames = useMemo(() => {
    const set = new Set(quotes.map((q) => q.clientName).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "it"));
  }, [quotes]);

  const filteredQuotes = useMemo(() => {
    return quotes.filter((q) => {
      if (statusFilter !== "all" && q.status !== statusFilter) return false;
      if (clientFilter !== "all" && q.clientName !== clientFilter) return false;
      if (yearFilter !== "all") {
        const y = q.issueDate ? new Date(q.issueDate).getFullYear() : null;
        if (y !== Number(yearFilter)) return false;
      }
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const matchNumber = (q.number || "").toLowerCase().includes(query);
        const matchClient = (q.clientName || "").toLowerCase().includes(query);
        if (!matchNumber && !matchClient) return false;
      }
      return true;
    });
  }, [quotes, statusFilter, clientFilter, yearFilter, searchQuery]);

  const hasActiveFilters =
    statusFilter !== "all" || clientFilter !== "all" || yearFilter !== "all" || searchQuery.trim() !== "";

  const clearFilters = () => {
    setStatusFilter("all");
    setClientFilter("all");
    setYearFilter("all");
    setSearchQuery("");
  };

  // Prossimo numero preventivo: PR-{seq}-{anno corrente}, seq = conteggio preventivi
  // emessi nell'anno corrente + 1.
  const nextNumber = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const count = quotes.filter(
      (q) => q.issueDate && new Date(q.issueDate).getFullYear() === currentYear
    ).length;
    return `PR-${String(count + 1).padStart(2, "0")}-${currentYear}`;
  }, [quotes]);

  const openCreate = () => {
    setEditingQuote(null);
    setFormOpen(true);
  };

  const openEdit = (quote) => {
    setEditingQuote(quote);
    setFormOpen(true);
  };

  const openDuplicate = (quote) => {
    const today = new Date().toISOString().split("T")[0];
    setEditingQuote({
      ...quote,
      id: undefined,
      number: "",
      issueDate: today,
      status: "draft",
      convertedInvoiceId: null,
    });
    setFormOpen(true);
  };

  const handleSave = async (payload, status) => {
    if (!uid) return;
    if (editingQuote?.id) {
      await updateQuote(uid, editingQuote.id, { ...payload, status: status || editingQuote.status });
    } else {
      await createQuote(uid, { ...payload, status: status || "draft" });
    }
    setFormOpen(false);
    setEditingQuote(null);
  };

  const handleDelete = async (quote) => {
    if (!uid) return;
    if (window.confirm(`Eliminare il preventivo "${quote.number || ""}"?`)) {
      await deleteQuote(uid, quote.id);
    }
  };

  const handleConvertToInvoice = async (quote) => {
    if (!uid) return;
    if (!window.confirm(`Convertire il preventivo "${quote.number}" in fattura?`)) return;
    const draft = buildInvoiceDraftFromQuote(quote);
    const newInvoiceId = await createInvoice(draft);
    await patchQuote(uid, quote.id, { status: "converted", convertedInvoiceId: newInvoiceId || null });
  };

  return (
    <div className={`min-h-screen transition-colors ${darkMode ? "bg-neutral-950 text-white" : "bg-neutral-50 text-neutral-900"}`}>
      <TopBar user={user} darkMode={darkMode} toggleDarkMode={toggleDarkMode} isSynced={isSynced} />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 pb-24">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className={`text-xl font-semibold flex items-center gap-2 ${darkMode ? "text-white" : "text-neutral-900"}`}>
              <FileCheck className="h-5 w-5" />
              Preventivi
            </h1>
            <p className={`mt-1 text-sm ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
              Crea preventivi, monitora lo stato e convertili in fattura quando accettati.
            </p>
          </div>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-forfy-500 text-white rounded-lg text-sm font-medium hover:bg-forfy-600 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Nuovo preventivo
          </button>
        </div>

        {/* Filtri */}
        <div className={`rounded-xl border p-4 mb-4 ${darkMode ? "bg-neutral-900 border-neutral-800" : "bg-white border-neutral-200"}`}>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              className={`px-3 py-2 rounded-lg border text-sm ${
                darkMode ? "border-neutral-700 bg-neutral-800 text-white" : "border-neutral-200 bg-white text-neutral-900"
              }`}
            >
              <option value="all">Tutti gli anni</option>
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-3 py-2 rounded-lg border text-sm ${
                darkMode ? "border-neutral-700 bg-neutral-800 text-white" : "border-neutral-200 bg-white text-neutral-900"
              }`}
            >
              <option value="all">Tutti gli stati</option>
              {QUOTE_STATUSES.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>

            <select
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              className={`px-3 py-2 rounded-lg border text-sm max-w-[180px] ${
                darkMode ? "border-neutral-700 bg-neutral-800 text-white" : "border-neutral-200 bg-white text-neutral-900"
              }`}
            >
              <option value="all">Tutti i clienti</option>
              {clientNames.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>

            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${darkMode ? "text-neutral-500" : "text-neutral-400"}`} />
              <input
                type="text"
                placeholder="Cerca numero o cliente..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`pl-9 pr-3 py-2 rounded-lg border text-sm w-56 ${
                  darkMode
                    ? "border-neutral-700 bg-neutral-800 text-white placeholder-neutral-500"
                    : "border-neutral-200 bg-white text-neutral-900 placeholder-neutral-400"
                }`}
              />
            </div>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm ${
                  darkMode ? "text-neutral-400 hover:bg-neutral-800" : "text-neutral-600 hover:bg-neutral-100"
                }`}
              >
                <X className="w-4 h-4" />
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Tabella preventivi */}
        <div className={`rounded-xl border overflow-hidden ${darkMode ? "border-neutral-800 bg-neutral-900" : "border-neutral-200 bg-white"}`}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className={`text-xs ${darkMode ? "bg-neutral-800 text-neutral-400" : "bg-neutral-50 text-neutral-600"}`}>
                <tr>
                  <th className="text-left px-4 py-3">Numero</th>
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Emissione</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Validità</th>
                  <th className="text-right px-4 py-3">Totale</th>
                  <th className="text-center px-4 py-3">Stato</th>
                  <th className="text-right px-4 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody className={darkMode ? "text-white" : "text-neutral-900"}>
                {loading ? (
                  <tr className={`border-t ${darkMode ? "border-neutral-800" : "border-neutral-200"}`}>
                    <td className={`px-4 py-8 text-center ${darkMode ? "text-neutral-500" : "text-neutral-500"}`} colSpan={7}>
                      Caricamento preventivi...
                    </td>
                  </tr>
                ) : filteredQuotes.length === 0 ? (
                  <tr className={`border-t ${darkMode ? "border-neutral-800" : "border-neutral-200"}`}>
                    <td className={`px-4 py-8 text-center ${darkMode ? "text-neutral-500" : "text-neutral-500"}`} colSpan={7}>
                      {hasActiveFilters
                        ? "Nessun preventivo corrisponde ai filtri selezionati."
                        : "Nessun preventivo ancora. Crea il primo!"}
                    </td>
                  </tr>
                ) : (
                  filteredQuotes.map((quote) => {
                    const style = STATUS_STYLES[quote.status] || STATUS_STYLES.draft;
                    return (
                      <tr key={quote.id} className={`border-t ${darkMode ? "border-neutral-800" : "border-neutral-200"}`}>
                        <td className="px-4 py-3 font-medium">{quote.number || "-"}</td>
                        <td className="px-4 py-3 max-w-[200px] truncate">{quote.clientName || "-"}</td>
                        <td className="px-4 py-3 hidden md:table-cell whitespace-nowrap">{formatDateIT(quote.issueDate)}</td>
                        <td className="px-4 py-3 hidden md:table-cell whitespace-nowrap">{formatDateIT(quote.validUntil)}</td>
                        <td className="px-4 py-3 text-right font-medium whitespace-nowrap">{formatCurrency(quote.total)}</td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                              darkMode ? style.dark : style.light
                            }`}
                          >
                            <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
                            {statusLabel(quote.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-2 flex-wrap justify-end">
                            {quote.status === "accepted" && (
                              <button
                                className={`text-xs px-2 py-1 rounded hover:underline ${darkMode ? "text-emerald-400" : "text-emerald-600"}`}
                                onClick={() => handleConvertToInvoice(quote)}
                              >
                                Converti in fattura
                              </button>
                            )}
                            <button
                              className={`text-xs px-2 py-1 rounded hover:underline ${darkMode ? "text-neutral-400" : "text-neutral-600"}`}
                              onClick={() => openDuplicate(quote)}
                            >
                              Duplica
                            </button>
                            <button
                              className={`text-xs px-2 py-1 rounded hover:underline ${darkMode ? "text-neutral-400" : "text-neutral-600"}`}
                              onClick={() => openEdit(quote)}
                            >
                              Modifica
                            </button>
                            <button
                              className={`text-xs px-2 py-1 rounded hover:underline ${darkMode ? "text-red-400" : "text-red-600"}`}
                              onClick={() => handleDelete(quote)}
                            >
                              Elimina
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className={`mt-3 text-xs ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
          {!loading && filteredQuotes.length > 0 && (
            <span>
              Mostrando {filteredQuotes.length} di {quotes.length} preventivi
              {hasActiveFilters && " (filtrati)"}
            </span>
          )}
        </div>
      </main>

      {formOpen && (
        <QuoteForm
          quote={editingQuote}
          onClose={() => {
            setFormOpen(false);
            setEditingQuote(null);
          }}
          onSave={handleSave}
          darkMode={darkMode}
          clients={clients}
          nextNumber={nextNumber}
        />
      )}
    </div>
  );
}
